/*****************************************************
 * Packages
 ******************************************************/
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import React, { useState, useMemo, useCallback } from 'react';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
/*****************************************************
 * Locals
 ******************************************************/
import Button from '../Button';
import IconFilter, { Option } from './reusable/IconFilter';

library.add(fas);
library.add(fab);
library.add(far);

const Icon = styled.span`
    font-size: 16px;
`;

const IconSelect = ({ onChange, name, value }) => {
    const [filter, setFilter] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [totalVisibleIcons, setTotalVisibleIcons] = useState(56);
    const allFaIcons = Object.values(far)
        .concat(Object.values(fas))
        .concat(Object.values(fab));
    const iconList = allFaIcons
        .map((icon) => ({ prefix: icon.prefix, iconName: icon.iconName }))
        .filter((icon) => icon.iconName.includes(filter))
        .filter((icon) => icon.iconName !== 'font-awesome-logo-full');

    const renderIconList = useMemo(
        () =>
            [null, ...iconList]
                .map((icon, ind) =>
                    icon ? (
                        <Option isIcon value={icon} key={ind}>
                            <Icon>
                                <FontAwesomeIcon
                                    icon={[icon.prefix, icon.iconName]}
                                />
                            </Icon>
                        </Option>
                    ) : (
                        <Option
                            isIcon
                            key={ind}
                            value={icon}
                            title="Remove Icon"
                        >
                            <img
                                src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iNDlweCIgaGVpZ2h0PSI0OXB4IiB2aWV3Qm94PSIwIDAgNDkgNDkiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8IS0tIEdlbmVyYXRvcjogU2tldGNoIDY0ICg5MzUzNykgLSBodHRwczovL3NrZXRjaC5jb20gLS0+CiAgICA8dGl0bGU+R3JvdXAgMzwvdGl0bGU+CiAgICA8ZGVzYz5DcmVhdGVkIHdpdGggU2tldGNoLjwvZGVzYz4KICAgIDxnIGlkPSJDYW52YXMiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJDYW52YXMtMS1Db3B5IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjEwLjAwMDAwMCwgLTI1My4wMDAwMDApIj4KICAgICAgICAgICAgPGcgaWQ9Ikdyb3VwLTMiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDIxMC45MDU2NjAsIDI1My45MDU2NjApIj4KICAgICAgICAgICAgICAgIDxsaW5lIHgxPSIzLjU0NzE2OTgxIiB5MT0iNC41NDcxNjk4MSIgeDI9IjQzLjUwMjEyNyIgeTI9IjQzLjczMzc2MjUiIGlkPSJMaW5lIiBzdHJva2U9IiNGRjNBMjgiIHN0cm9rZS13aWR0aD0iNSIgc3Ryb2tlLWxpbmVjYXA9InNxdWFyZSI+PC9saW5lPgogICAgICAgICAgICAgICAgPGxpbmUgeDE9IjQzLjY0MTUwOTQiIHkxPSI0LjM1ODQ5MDU3IiB4Mj0iNC43MjI0NTE5IiB5Mj0iNDMuMjc3NTQ4MSIgaWQ9IkxpbmUtMiIgc3Ryb2tlPSIjRkYzQTI4IiBzdHJva2Utd2lkdGg9IjUiIHN0cm9rZS1saW5lY2FwPSJzcXVhcmUiPjwvbGluZT4KICAgICAgICAgICAgICAgIDxyZWN0IGlkPSJSZWN0YW5nbGUiIHN0cm9rZT0iI0U1RUJGMCIgc3Ryb2tlLXdpZHRoPSIzIiB4PSIxLjUiIHk9IjEuNSIgd2lkdGg9IjQ1IiBoZWlnaHQ9IjQ1IiByeD0iNCI+PC9yZWN0PgogICAgICAgICAgICA8L2c+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4="
                                width="17"
                                alt=""
                            />
                        </Option>
                    )
                )
                .slice(0, totalVisibleIcons),
        [iconList, totalVisibleIcons]
    );

    const handleFilter = useCallback(
        debounce((payload) => {
            setFilter(payload.value.toLowerCase());
        }, 300),
        []
    );

    const loadMore = useCallback(() => {
        setLoading(true);
        setTimeout(() => {
            setTotalVisibleIcons(iconList.length);
            setLoading(false);
        });
    }, [iconList.length]);

    return (
        <IconFilter
            cols="8"
            name={name}
            value={value}
            onSelect={onChange}
            onFilter={handleFilter}
            placeholder="Search for Icon"
        >
            {renderIconList}
            {iconList.length > Number(totalVisibleIcons) && (
                <Button
                    type="link"
                    onClick={loadMore}
                    style={{ width: '100%', color: '#0863ff' }}
                >
                    {isLoading ? (
                        <FontAwesomeIcon
                            spin
                            fixedWidth
                            icon={['far', 'spinner']}
                        />
                    ) : (
                        '+'
                    )}{' '}
                    Load all icons
                </Button>
            )}
        </IconFilter>
    );
};
export default IconSelect;
